import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin } from 'lucide-react';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom orange marker icon
const orangeIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
      <path fill="#E67E22" stroke="#fff" stroke-width="2" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.4 12.5 28.5 12.5 28.5S25 20.9 25 12.5C25 5.6 19.4 0 12.5 0z"/>
      <circle cx="12.5" cy="12.5" r="5" fill="#fff"/>
    </svg>
  `),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

// Component to handle map clicks
function MapClickHandler({ onLocationSelect }) {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng);
    },
  });
  return null;
}

export default function MapPicker({ privacyMode, onLocationSelect, initialLocation }) {
  const [position, setPosition] = useState(initialLocation || null);
  const [loading, setLoading] = useState(!initialLocation);
  const mapRef = useRef(null);
  const manualRef = useRef(false);       // bruker har klikket på kartet selv
  const bestAccuracyRef = useRef(Infinity); // beste (laveste) nøyaktighet vi har sett

  useEffect(() => {
    if (initialLocation || !navigator.geolocation) return;

    let watchId = null;
    let maxTimer = null;
    let stopped = false;
    let gotFix = false;

    const stopWatch = () => {
      if (stopped) return;
      stopped = true;
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      if (maxTimer) clearTimeout(maxTimer);
    };

    // watchPosition gir flere målinger over tid. GPS starter ofte grovt
    // (celle/WiFi, hundrevis av meter) og konvergerer til få meter når
    // satellittfix er på plass. Vi beholder kun målingen med best (lavest)
    // nøyaktighet og lar den fortsette å finjustere mens bruker fyller ut
    // skjemaet — det er da den virkelig blir presis.
    const onReading = (pos) => {
      if (manualRef.current) { stopWatch(); return; } // bruker har overstyrt
      gotFix = true;
      setLoading(false); // vis kartet så snart vi har en posisjon
      const acc = pos.coords.accuracy ?? Infinity;
      if (acc < bestAccuracyRef.current) {
        bestAccuracyRef.current = acc;
        const newPos = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setPosition(newPos);
        onLocationSelect(newPos); // auto-lagre beste posisjon så langt
      }
      if (acc <= 15) stopWatch(); // utmerket fix → ikke tapp batteri videre
    };

    const onError = (error) => {
      console.error('Geolocation error:', error);
      if (!gotFix && !manualRef.current) {
        // Fallback til Norge (sentrum) — ingen auto-lagring, bruker må klikke.
        setPosition({ lat: 60.472, lng: 8.4689 });
      }
      setLoading(false);
      if (!gotFix) stopWatch(); // har vi allerede en fix, behold og fortsett
    };

    watchId = navigator.geolocation.watchPosition(onReading, onError, {
      // Be om ekte GPS i stedet for grovt WiFi/IP-estimat.
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 0,
    });

    // La GPS konvergere i opptil 45 s (mens skjemaet fylles ut), så stopp for
    // å spare batteri. Den beste fixen i mellomtiden er allerede lagret.
    maxTimer = setTimeout(stopWatch, 45000);

    return stopWatch;
  }, [initialLocation]);

  const handleLocationSelect = (latlng) => {
    manualRef.current = true;  // stopp GPS-oppdatering, bruker bestemmer nå
    setPosition(latlng);
    onLocationSelect(latlng);
  };

  // Beregn radius basert på privacy mode
  const getRadius = () => {
    switch(privacyMode) {
      case 'Nøyaktig': return 0;
      case '100m': return 100;
      case '1km': return 1000;
      default: return 1000;
    }
  };

  if (loading || !position) {
    return (
      <div className="h-64 bg-[#E6E2D8] rounded-md border border-[#D1CCC0] flex items-center justify-center">
        <div className="text-center space-y-2">
          <div className="w-8 h-8 border-4 border-[#3B4D3C] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-[10px] font-black uppercase text-[#3B4D3C]/60 tracking-widest">
            Henter nøyaktig posisjon...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-64 rounded-md overflow-hidden border border-[#D1CCC0] shadow-inner z-10">
      <MapContainer
        center={[position.lat, position.lng]}
        zoom={13}
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapClickHandler onLocationSelect={handleLocationSelect} />
        
        {privacyMode === 'Nøyaktig' ? (
          <Marker position={[position.lat, position.lng]} icon={orangeIcon} />
        ) : (
          <Circle
            center={[position.lat, position.lng]}
            radius={getRadius()}
            pathOptions={{
              color: '#E67E22',
              fillColor: '#E67E22',
              fillOpacity: 0.2,
              weight: 2
            }}
          />
        )}
      </MapContainer>
      
      {/* Info overlay */}
      <div className="absolute bottom-2 left-2 right-2 z-[1000] bg-white/95 backdrop-blur-sm p-3 rounded shadow-lg border border-[#D1CCC0]/50">
        <div className="flex items-center justify-between">
          <span className="text-[9px] font-black uppercase tracking-widest text-[#3B4D3C]">
            Klikk for å endre
          </span>
          <span className="text-[8px] font-bold text-[#E67E22] uppercase">
            {privacyMode}
          </span>
        </div>
      </div>
    </div>
  );
}
